/**
 * `annotationDto.ts` — Unity wire shapes for annotation (comment) + reply rows,
 * plus the sort-key builders and DTO↔`AnnotationData` mappers.
 *
 * Source of truth: `DynamoClient.cs` annotation methods (:3269-3790),
 * `Models/Data/AnnotationData.cs`, `Models/Data/AnnotationReplyData.cs`. All
 * rows share `pk = "{customerId}:project"`; the sort key carries the ids. The
 * web port only models the base (non-instanced) sort key — Unity's optional
 * `U#{learnerCode}` prefix (instanced projects) is not modeled here.
 *
 * Key contract facts the media-tile port taught us to respect verbatim:
 *   - Annotation text lives in `AnnotationText`; reply text lives in `Message`.
 *   - Reply timestamps use `CreatedTS` / `ModifiedTS` — NOT `…Timestamp`.
 *   - `IsResolved` is an integer `N` (0 = open, 1 = resolved), NOT a BOOL.
 *   - Annotation rows and their reply rows share one `annotation:{Min}`..`{Max}`
 *     BETWEEN window; a row is a reply iff its sk contains `:reply:`
 *     (`DynamoClient.GetAnnotations`:3453).
 *   - `AnnotationType` int: 0 = TextAnnotation (a plain comment).
 */

import type {
  AnnotationAuthor,
  AnnotationData,
  AnnotationQuat,
  AnnotationReplyData,
  AnnotationVec3,
} from "./annotationData";

/**
 * `AnnotationData.AnnotationType` enum ints (AnnotationData.cs:10-15). Stored
 * as Dynamo `N`. A plain text comment is `Text` (0), which is also the field
 * default, so an absent attribute reads as text.
 */
export const ANNOTATION_TYPE = {
  Text: 0,
  Sketch: 1,
  Collision: 2,
  ActionData: 3,
} as const;

/**
 * Annotation row wire shape (`DynamoClient.ToAnnotationItem`:3343 new-format).
 * Detached text comments omit the `AttachmentPosition`/`PlacementPosition`/
 * `PlacementRotation` maps and the `ParentEntitySnapshotIds`/`Forces`/`PosVecs`
 * lists (Unity's `SafeSet*` skips empties), so they're not modeled here.
 */
export interface AnnotationDto {
  readonly ID: string;
  readonly AnnotationType?: number;
  /** Integer `N`, 0 = open / 1 = resolved (NOT a BOOL). */
  readonly IsResolved?: number;
  readonly IsSceneVisible?: boolean;
  readonly Fov?: number;
  readonly OrthographicSize?: number;
  readonly AnnotationText?: string;
  /** Empty ⇒ detached (free-floating) comment. */
  readonly ParentEntitySnapshotId?: string;
  readonly CreatorId?: string;
  readonly CreatorNaturalAccountId?: string;
  readonly CreatorInitials?: string;
  readonly CreatorName?: string;
  readonly CreatedTimestamp?: string;
  readonly ModifiedTimestamp?: string;
  readonly SceneID?: string;
  readonly VisualizationType?: number;
  readonly ActionDataId?: string;
  readonly isExplosionAnnotation?: boolean;
  /**
   * Sketch validity gate (BOOL). CRITICAL read default: Unity's new-format
   * reader defaults an ABSENT attribute to TRUE (`DynamoClient.cs:3330`),
   * which is why `CreateAnnotationAsync` follows every sketch create with an
   * explicit `SketchUploaded = false` update (`ProjectDataModel.cs:2858-2871`).
   * Never written by the create Put itself.
   */
  readonly SketchUploaded?: boolean;
  /**
   * Marker geometry, Dynamo `M` maps (`SafeSetVectorAttribute`, keys x/y/z(/w),
   * `N` values). Omitted for detached comments (empty maps skipped on write).
   * After `unmarshall` these are plain `{x,y,z}` / `{x,y,z,w}` number objects.
   */
  readonly AttachmentPosition?: unknown;
  readonly PlacementPosition?: unknown;
  readonly PlacementRotation?: unknown;
  readonly [extra: string]: unknown;
}

/**
 * Reply row wire shape (`DynamoClient.ToAnnotationReplyItem`:3649). Reply text
 * is `Message`; timestamps are `CreatedTS`/`ModifiedTS`. `AnnotationId` is a
 * synthetic field (NOT a stored attribute) — the storage layer parses it from
 * the reply sort key so the model can bucket replies to their parent.
 */
export interface AnnotationReplyDto {
  readonly ID: string;
  readonly Message?: string;
  readonly CreatedTS?: string;
  readonly ModifiedTS?: string;
  readonly CreatorId?: string;
  readonly CreatorNaturalAccountId?: string;
  readonly CreatorInitials?: string;
  readonly CreatorName?: string;
  /** Parent annotation id, parsed from the reply sk (synthetic). */
  readonly AnnotationId?: string;
  readonly [extra: string]: unknown;
}

/** Flat result of the shared annotation+reply BETWEEN query. */
export interface AnnotationQueryResult {
  readonly annotations: ReadonlyArray<AnnotationDto>;
  readonly replies: ReadonlyArray<AnnotationReplyDto>;
}

// -- Sort keys (DynamoClient.cs:92-115; base sk, no learnerCode prefix) ------

/** `#project:{projectId}:annotation:{annotationId}` */
export function annotationSk(projectId: string, annotationId: string): string {
  return `#project:${projectId}:annotation:${annotationId}`;
}

/** `#project:{projectId}:annotation:{annotationId}:reply:{replyId}` (parentKey = annotation) */
export function annotationReplySk(
  projectId: string,
  annotationId: string,
  replyId: string,
): string {
  return `#project:${projectId}:annotation:${annotationId}:reply:${replyId}`;
}

/** The literal segment that distinguishes a reply row from an annotation row. */
export const ANNOTATION_REPLY_SK_SEGMENT = ":reply:";

/** Extract the parent annotation id from a reply sort key. */
export function parseAnnotationIdFromReplySk(sk: string): string | null {
  const m = /:annotation:([^:]+):reply:/.exec(sk);
  return m ? m[1] : null;
}

// -- Vector / quaternion map (Dynamo `M`) helpers ----------------------------

function vec3FromMap(m: unknown): AnnotationVec3 | undefined {
  if (!m || typeof m !== "object") return undefined;
  const o = m as Record<string, unknown>;
  const x = Number(o.x);
  const y = Number(o.y);
  const z = Number(o.z);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return undefined;
  // An all-zero marker pose means "no pose", not "at the world origin".
  // Unity seeds DUMMY ZERO vectors when creating any annotation (its
  // `OnAfterDeserialize` throws on a missing key — see the same rule in
  // `annotationWire.vec3FromPairs`), and `SafeSetVectorAttribute` persists
  // that non-empty dictionary, so a Unity-authored LIST-ONLY comment carries
  // `PlacementPosition = {0,0,0}`. Reading it literally makes the renderer —
  // which treats "has a placement pose" as "is anchored" — draw a phantom
  // bubble at the origin for every detached comment.
  //
  // NOTE the renderer must NOT gate on `isDetached` instead: the web's own
  // anchored markers are deliberately detached (world-space pose + empty
  // parent snapshot id, see `annotationData.ts`), so that would hide them all.
  if (x === 0 && y === 0 && z === 0) return undefined;
  return [x, y, z];
}

function quatFromMap(m: unknown): AnnotationQuat | undefined {
  if (!m || typeof m !== "object") return undefined;
  const o = m as Record<string, unknown>;
  const x = Number(o.x);
  const y = Number(o.y);
  const z = Number(o.z);
  const w = Number(o.w);
  if (![x, y, z, w].every((n) => Number.isFinite(n))) return undefined;
  return [x, y, z, w];
}

/** `[x,y,z] → {x,y,z}` — the shape `marshall` turns into a Dynamo `M`. */
export function vec3ToMap(v: AnnotationVec3): { x: number; y: number; z: number } {
  return { x: v[0], y: v[1], z: v[2] };
}

export function quatToMap(q: AnnotationQuat): { x: number; y: number; z: number; w: number } {
  return { x: q[0], y: q[1], z: q[2], w: q[3] };
}

// -- DTO ↔ AnnotationData mappers --------------------------------------------

function toAuthor(
  id: string | undefined,
  name: string | undefined,
  initials: string | undefined,
  naturalAccountId: string | undefined,
): AnnotationAuthor {
  return {
    id: id ?? "",
    name: name ?? "",
    initials: initials ?? "",
    naturalAccountId: naturalAccountId || undefined,
  };
}

/**
 * Map a stored annotation row (+ its already-bucketed replies) into the
 * UI-shaped {@link AnnotationData}. Text and Sketch rows are modeled;
 * Collision/ActionData rows are filtered upstream by the model loader.
 */
export function mapAnnotationDtoToData(
  dto: AnnotationDto,
  replies: ReadonlyArray<AnnotationReplyDto>,
): AnnotationData {
  const isSketch = Number(dto.AnnotationType ?? 0) === ANNOTATION_TYPE.Sketch;
  return {
    id: dto.ID,
    sceneId: dto.SceneID ?? "",
    type: isSketch ? "sketch" : "text",
    text: dto.AnnotationText ?? "",
    creator: toAuthor(dto.CreatorId, dto.CreatorName, dto.CreatorInitials, dto.CreatorNaturalAccountId),
    createdAt: dto.CreatedTimestamp ?? "",
    modifiedAt: dto.ModifiedTimestamp ?? dto.CreatedTimestamp ?? "",
    // Unity `IsResolved` is an int; !=0 ⇒ resolved (absent ⇒ 0 ⇒ open).
    isResolved: Number(dto.IsResolved ?? 0) !== 0,
    // Detached ⇒ no parent snapshot (AnnotationData.IsDetached, :237).
    isDetached: !(dto.ParentEntitySnapshotId && dto.ParentEntitySnapshotId.length > 0),
    parentSnapshotId: dto.ParentEntitySnapshotId || undefined,
    replies: replies.map(mapAnnotationReplyDtoToData),
    attachmentPosition: vec3FromMap(dto.AttachmentPosition),
    placementPosition: vec3FromMap(dto.PlacementPosition),
    placementRotation: quatFromMap(dto.PlacementRotation),
    // Sketch fields. `SketchUploaded` absent ⇒ TRUE (Unity new-format read
    // default, DynamoClient.cs:3330) — the create flow's explicit `false`
    // follow-up write is what keeps half-created sketches hidden.
    sketchUploaded: isSketch ? dto.SketchUploaded !== false : undefined,
    fov: isSketch ? Number(dto.Fov ?? 60) : undefined,
    orthographicSize: isSketch ? Number(dto.OrthographicSize ?? 0) : undefined,
  };
}

export function mapAnnotationReplyDtoToData(dto: AnnotationReplyDto): AnnotationReplyData {
  return {
    id: dto.ID,
    parentId: dto.AnnotationId ?? "",
    message: dto.Message ?? "",
    creator: toAuthor(dto.CreatorId, dto.CreatorName, dto.CreatorInitials, dto.CreatorNaturalAccountId),
    createdAt: dto.CreatedTS ?? "",
    modifiedAt: dto.ModifiedTS ?? dto.CreatedTS ?? "",
  };
}

/**
 * Build the annotation row for a create. The web port writes detached text
 * comments and camera-posed sketches, so `ParentEntitySnapshotId`/
 * `ActionDataId` are always empty and the position maps are omitted when
 * absent (matching Unity's `SafeSet*` skip). Camera fields default to Unity's
 * (`Fov = 60`, `OrthographicSize = 0`); sketches carry the capture camera's
 * real values. `SketchUploaded` is deliberately NOT written here — Unity
 * parity requires the separate `false` follow-up update after the Put.
 */
export function annotationDataToDto(data: AnnotationData): AnnotationDto {
  const dto: AnnotationDto = {
    ID: data.id,
    AnnotationType: data.type === "sketch" ? ANNOTATION_TYPE.Sketch : ANNOTATION_TYPE.Text,
    IsResolved: data.isResolved ? 1 : 0,
    IsSceneVisible: true,
    Fov: data.fov ?? 60,
    OrthographicSize: data.orthographicSize ?? 0,
    AnnotationText: data.text,
    ParentEntitySnapshotId: "",
    CreatorId: data.creator.id,
    CreatorNaturalAccountId: data.creator.naturalAccountId ?? "",
    CreatorInitials: data.creator.initials,
    CreatorName: data.creator.name,
    CreatedTimestamp: data.createdAt,
    ModifiedTimestamp: data.modifiedAt,
    SceneID: data.sceneId,
    VisualizationType: 0,
    ActionDataId: "",
    isExplosionAnnotation: false,
  };
  // Scene-placed marker → write the world-space position maps (empty maps
  // are omitted for detached list-only comments, matching Unity's
  // `SafeSetVectorAttribute` skip-empty).
  if (data.placementPosition) {
    return {
      ...dto,
      AttachmentPosition: vec3ToMap(data.attachmentPosition ?? data.placementPosition),
      PlacementPosition: vec3ToMap(data.placementPosition),
      PlacementRotation: quatToMap(data.placementRotation ?? [0, 0, 0, 1]),
    };
  }
  return dto;
}

export function annotationReplyDataToDto(data: AnnotationReplyData): AnnotationReplyDto {
  return {
    ID: data.id,
    Message: data.message,
    CreatedTS: data.createdAt,
    ModifiedTS: data.modifiedAt,
    CreatorId: data.creator.id,
    CreatorNaturalAccountId: data.creator.naturalAccountId ?? "",
    CreatorInitials: data.creator.initials,
    CreatorName: data.creator.name,
    AnnotationId: data.parentId,
  };
}
